import classNames from "clsx";
import * as React from "react";
import { Text } from "@radix-ui/themes";
import { Slot } from "radix-ui";
import { extractProps } from "@radix-ui/themes/helpers";
import type {
  ComponentPropsWithout,
  RemovedProps,
} from "@radix-ui/themes/helpers";
import type { GetPropDefTypes, MarginProps } from "@radix-ui/themes/props";
import {
  dataListItemPropDefs,
  dataListLabelPropDefs,
  dataListRootPropDefs,
  dataListValuePropDefs,
} from "./data-list.props.js";

type DataListRootElement = HTMLDListElement;
type DataListRootOwnProps = GetPropDefTypes<typeof dataListRootPropDefs>;
interface CommonDataListRootProps extends MarginProps, DataListRootOwnProps {}
type DataListRootDivProps = { as?: "div" } & ComponentPropsWithout<
  "div",
  RemovedProps
>;
type DataListRootDlProps = { as?: "dl" } & ComponentPropsWithout<
  "dl",
  RemovedProps
>;
type DataListRootProps = CommonDataListRootProps &
  (DataListRootDivProps | DataListRootDlProps);

const DataListRoot = React.forwardRef<DataListRootElement, DataListRootProps>(
  (props, forwardedRef) => {
    const {
      children,
      className,
      asChild,
      as: Tag = "dl",
      ...dataListProps
    } = extractProps(props, dataListRootPropDefs);
    return (
      <Text asChild>
        <Slot.Root
          {...dataListProps}
          ref={forwardedRef}
          className={classNames("rt-DataListRoot", className)}
        >
          {asChild ? children : <Tag>{children}</Tag>}
        </Slot.Root>
      </Text>
    );
  },
);
DataListRoot.displayName = "DataList.Root";

type DataListItemElement = HTMLDivElement;
type DataListItemOwnProps = GetPropDefTypes<typeof dataListItemPropDefs>;
interface DataListItemProps
  extends ComponentPropsWithout<"div", RemovedProps>, DataListItemOwnProps {}
const DataListItem = React.forwardRef<DataListItemElement, DataListItemProps>(
  (props, forwardedRef) => {
    const { children, className, asChild, ...itemProps } = extractProps(
      props,
      dataListItemPropDefs,
    );
    return (
      <Slot.Root
        {...itemProps}
        ref={forwardedRef}
        className={classNames("rt-DataListItem", className)}
      >
        {asChild ? children : <div>{children}</div>}
      </Slot.Root>
    );
  },
);
DataListItem.displayName = "DataList.Item";

type DataListLabelElement = React.ComponentRef<"dt">;
type DataListLabelOwnProps = GetPropDefTypes<typeof dataListLabelPropDefs>;
interface CommonDataListLabelProps extends MarginProps, DataListLabelOwnProps {}
type DataListLabelDtProps = { as?: "dt" } & ComponentPropsWithout<
  "dt",
  RemovedProps
>;
type DataListLabelDivProps = { as?: "div" } & ComponentPropsWithout<
  "div",
  RemovedProps
>;
type DataListLabelProps = CommonDataListLabelProps &
  (DataListLabelDtProps | DataListLabelDivProps);

const DataListLabel = React.forwardRef<
  DataListLabelElement,
  DataListLabelProps
>((props, forwardedRef) => {
  const {
    asChild,
    as: Tag = "dt",
    children,
    className,
    color,
    ...labelProps
  } = extractProps(props, dataListLabelPropDefs);
  return (
    <Slot.Root
      {...labelProps}
      data-accent-color={color}
      ref={forwardedRef}
      className={classNames("rt-DataListLabel", className)}
    >
      {asChild ? children : <Tag>{children}</Tag>}
    </Slot.Root>
  );
});
DataListLabel.displayName = "DataList.Label";

type DataListValueElement = React.ComponentRef<"dd">;
type DataListValueOwnProps = GetPropDefTypes<typeof dataListValuePropDefs>;
interface CommonDataListValueProps extends MarginProps, DataListValueOwnProps {}
type DataListValueDdProps = { as?: "dd" } & ComponentPropsWithout<
  "dd",
  RemovedProps
>;
type DataListValueDivProps = { as?: "div" } & ComponentPropsWithout<
  "div",
  RemovedProps
>;
type DataListValueProps = CommonDataListValueProps &
  (DataListValueDdProps | DataListValueDivProps);
const DataListValue = React.forwardRef<
  DataListValueElement,
  DataListValueProps
>((props, forwardedRef) => {
  const {
    children,
    className,
    asChild,
    as: Tag = "dd",
    ...valueProps
  } = extractProps(props, dataListValuePropDefs);
  return (
    <Slot.Root
      {...valueProps}
      ref={forwardedRef}
      className={classNames(className, "rt-DataListValue")}
    >
      {asChild ? children : <Tag>{children}</Tag>}
    </Slot.Root>
  );
});
DataListValue.displayName = "DataList.Value";

export {
  DataListRoot as Root,
  DataListItem as Item,
  DataListLabel as Label,
  DataListValue as Value,
};
export type {
  DataListRootElement as RootElement,
  DataListRootProps as RootProps,
  DataListItemElement as ItemElement,
  DataListItemProps as ItemProps,
  DataListLabelElement as LabelElement,
  DataListLabelProps as LabelProps,
  DataListValueElement as ValueElement,
  DataListValueProps as ValueProps,
};
